import { Component, OnInit, OnDestroy, DoCheck } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Pagination, ContentProvider, Company } from '@lighty';
import { EmitterService, ParamsService, LoaderService, StorageService, TranslateService } from '@services';
import { SubscriptionCollection } from '@classes';
import { DataHelper } from '@helpers';

@Component({
    selector: 'msc-learn-content-saved',
    templateUrl: './saved.component.html',
    styleUrls: ['./saved.component.scss']
})
export class LearnContentSavedComponent implements OnInit, OnDestroy, DoCheck {
    private subscriptionCollection: SubscriptionCollection = new SubscriptionCollection();
    private me: any;
    private company: Company;
    public contents: any;
    public pagination: Pagination;
    public searching: boolean = false;
    public loading: boolean = false;
    public query: string;
    public filters: any = {};
    public activeFilters: boolean = false;
    public cardParams: any;
    // Propiedades para el carrusel de filtros
    public availableTypes: any[] = [];
    public availableLanguages: any[] = [];
    public availableProviders: any[];
    public filtersModal: any;
    // Control de botones de navegación del carrusel
    public canScrollLeft: boolean = false;
    public canScrollRight: boolean = false;
    // Variables para rastrear cambios de filtros desde Filter1
    private lastFilterState: any = null;

    constructor(private route: ActivatedRoute, private contentProvider: ContentProvider, private storageService: StorageService,
        private loaderService: LoaderService, private emitterService: EmitterService, private paramsService: ParamsService,
        private translateService: TranslateService) { }

    ngOnInit(): void {
        this.me = this.storageService.get('me');
        this.company = this.storageService.get('company');
        this.contents = this.route.snapshot.data.contents.contents.map(content => ({ ...content, saved: true }));
        this.pagination = this.route.snapshot.data.contents.pagination;

        this.cardParams = {
            saved: true
        };

        this.subscriptionCollection.subscribe = this.emitterService.get('search.contents').subscribe((query) => {
            if (typeof query === 'string') {
                this.contents = null;
                this.search(query);
                this.emitterService.set('search.contents', {});
            }
        });

        this.getMeta(); // Cargar filtros para el carrusel

        // AJUSTE 2: Suscribirse al evento de reset del topbar-filters
        this.subscriptionCollection.subscribe = this.emitterService.get('filter.reset.carousel').subscribe(() => {
            this.resetCarouselFilters();
            this.search();
        });
    }

    ngOnDestroy(): void {
        this.subscriptionCollection.unsubscribe();
    }

    ngDoCheck(): void {
        // Sincronizar cambios de filtros desde Filter1 (topbar-filters) a Filter2 (carousel)
        // Comparar si el array de contentTypes o providers cambió
        const filtersChanged = !this.lastFilterState || 
                              JSON.stringify(this.lastFilterState.contentTypes) !== JSON.stringify(this.filters.contentTypes) ||
                              JSON.stringify(this.lastFilterState.providers) !== JSON.stringify(this.filters.providers);
        
        if (filtersChanged) {
            this.syncFiltersFromTopbar();
            // Guardar estado actual
            this.lastFilterState = {
                contentTypes: this.filters.contentTypes ? [...this.filters.contentTypes] : [],
                providers: this.filters.providers ? [...this.filters.providers] : []
            };
        }
    }

    private syncFiltersFromTopbar(): void {
        // Actualizar directamente sin mapear todo el array
        if (this.filters.contentTypes && this.filters.contentTypes.length > 0) {
            for (let type of this.availableTypes) {
                type.isSelected = this.filters.contentTypes.includes(type.value);
            }
        } else {
            for (let type of this.availableTypes) {
                type.isSelected = false;
            }
        }

        // Actualizar providers sin regenerar el array
        if (this.filters.providers && this.filters.providers.length > 0) {
            for (let provider of this.availableProviders) {
                provider.isSelected = this.filters.providers.includes(provider.id);
            }

            // Actualizar también en filtersModal
            if (this.filtersModal) {
                for (let filter of this.filtersModal) {
                    if (filter.name === 'providers' && filter.options) {
                        for (let option of filter.options) {
                            option.isSelected = this.filters.providers.includes(option.id);
                        }
                    }
                }
            }
        } else {
            for (let provider of this.availableProviders) {
                provider.isSelected = false;
            }

            // Actualizar también en filtersModal
            if (this.filtersModal) {
                for (let filter of this.filtersModal) {
                    if (filter.name === 'providers' && filter.options) {
                        for (let option of filter.options) {
                            option.isSelected = false;
                        }
                    }
                }
            }
        }
    }
    search(query?: string): void {
        this.searching = true;
        this.loaderService.run();

        const params = this.paramsService.getCatalogue(this.filters, this.company.id, this.query || query, null, null, this.me);

        this.contentProvider.getSavedContents(params).subscribe((data) => {
            this.pagination = data.pagination;
            this.contents = data.contents.map(content => ({ ...content, saved: true }));
            this.searching = false;
        });
    }

    loadMore(): void {
        this.loading = true;
        this.loaderService.run();

        const params = this.paramsService.getCatalogue(this.filters, this.company.id, null, this.pagination, null, this.me);

        this.contentProvider.getSavedContents(params).subscribe((data) => {
            this.pagination = data.pagination;
            this.contents = this.contents.concat(data.contents.map(content => ({ ...content, saved: true })));
            this.loading = false;
        });
    }

    // Métodos para el carrusel de filtros
    private getMeta(): void {
        this.contentProvider.getMeta('saved-content').subscribe((meta) => {
            this.availableTypes = this.getTypesOptions(meta);
            this.availableLanguages = this.getLanguagesOptions(meta.languages);
            this.availableProviders = this.getProvidersOptions(meta.providers);
            this.setFilters();
        });
    }

    private getTypesOptions(data: any) {
        return Object.keys(data.contentTypes)
            .map((type) => ({
                id: `type-${type}`,
                label: this.translateService.instant(`api.content-type.${type.split(/(?=[A-Z])/).join('_').toLowerCase()}`),
                value: type.split(/(?=[A-Z])/).join('_').toLowerCase(),
                isSelected: false
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    private getProvidersOptions(data: any) {
        const label = this.company?.experience
            ? (this.company.experience.displayName ? this.company.experience.displayName : this.company.experience.name)
            : this.company?.name;

        return data
            .filter(provider => provider && (provider.id === 'company' || provider.name))
            .map((provider) => ({
                id: provider.id,
                label: provider.id === 'company' ? label : provider.name,
                isSelected: false
            }));
    }

    private getLanguagesOptions(data: any) {
        return Object.keys(data).map((lang) => ({
            id: lang,
            label: data[lang],
            isSelected: false
        }));
    }

    private setFilters(): void {
        this.filtersModal = [
            {
                name: 'langs',
                type: 'multiselect',
                label: this.translateService.instant('words.languages'),
                displayValue: this.availableLanguages.filter(lang => lang.isSelected).map(lang => lang.label).join(', '),
                options: this.availableLanguages
            },
            {
                name: 'providers',
                type: 'checkbox',
                label: this.translateService.instant('words.providers'),
                options: this.availableProviders
            }
        ];
    }

    public onSelectType(option) {
        // El carousel ya toglea isSelected, solo necesitamos actualizar filtros
        // Si hay tipos de contenido seleccionados → limpiar proveedores (Grupo 2)
        const hasSelectedTypes = this.availableTypes.some(type => type.isSelected);
        if (hasSelectedTypes) {
            this.clearProvidersFilter();
        }
        this.saveFilters();
    }

    public handleInlineMultiselectChange(event: any) {
        const { option, filterName, optionsArray } = event;
        
        // El carousel ya toglea isSelected, solo necesitamos actualizar filtros
        const selectedValues = optionsArray
            .filter(o => o.isSelected)
            .map(o => o.id);

        // Actualizar el filtro
        this.filters[filterName] = selectedValues;

        // Si filterName es 'providers' y hay proveedores seleccionados → limpiar tipos de contenido (Grupo 1)
        if (filterName === 'providers' && selectedValues.length > 0) {
            this.clearContentTypesFilter();
        }

        this.saveFilters();
    }

    private saveFilters(): void {
        // Construir objeto de filtros de una sola vez
        const contentTypeValues = this.availableTypes.filter(t => t.isSelected).map(t => t.value);

        // Crear nuevo objeto para forzar change detection en msc-shared-topbar-filters
        this.filters = Object.assign({}, this.filters, {
            contentTypes: contentTypeValues.length > 0 ? contentTypeValues : [],
            providers: this.filters.providers || []
        });

        // Activar el botón reset si hay filtros seleccionados
        this.activeFilters = contentTypeValues.length > 0 || (this.filters.providers && this.filters.providers.length > 0);

        console.log('saveFilters - contentTypes enviados:', this.filters.contentTypes);
        console.log('saveFilters - providers enviados:', this.filters.providers);
        this.search();
    }

    // Métodos para limpiar filtros cruzados
    private clearProvidersFilter(): void {
        // Crear nuevo array para forzar Angular change detection
        const clearedProviders = this.availableProviders.map(provider => ({
            ...provider,
            isSelected: false
        }));
        this.availableProviders = [...clearedProviders];
        // Limpiar el filtro en filters
        this.filters = DataHelper.merge(this.filters, { providers: [] });
    }

    private clearContentTypesFilter(): void {
        // Crear nuevo array para forzar Angular change detection
        const clearedTypes = this.availableTypes.map(type => ({
            ...type,
            isSelected: false
        }));
        this.availableTypes = [...clearedTypes];
        this.filters = DataHelper.merge(this.filters, { contentTypes: [] });
    }

    // Método para resetear filtros del carrusel
    public resetCarouselFilters(): void {
        // Resetear tipos de contenido
        this.availableTypes = this.availableTypes.map(type => ({
            ...type,
            isSelected: false
        }));

        // Resetear proveedores
        this.availableProviders = this.availableProviders.map(provider => ({
            ...provider,
            isSelected: false
        }));

        // Actualizar también en filtersModal si existe
        if (this.filtersModal) {
            this.filtersModal = this.filtersModal.map(filter => {
                if (filter.type === 'checkbox' && filter.options.length) {
                    filter.options = filter.options.map(option => ({
                        ...option,
                        isSelected: false
                    }));
                }
                return filter;
            });
        }

        // Limpiar los filtros aplicados
        this.filters = {
            contentTypes: [],
            providers: []
        };
        this.activeFilters = false;
    }

    public scrollCategoriesLeft(): void {
        const container = document.getElementById('category-container');
        if (container) {
            container.scrollBy({ left: -200, behavior: 'smooth' });
            setTimeout(() => this.updateScrollButtons(), 300);
        }
    }

    public scrollCategoriesRight(): void {
        const container = document.getElementById('category-container');
        if (container) {
            container.scrollBy({ left: 200, behavior: 'smooth' });
            setTimeout(() => this.updateScrollButtons(), 300);
        }
    }

    public onCategoryContainerScroll(): void {
        this.updateScrollButtons();
    }

    private updateScrollButtons(): void {
        const container = document.getElementById('category-container');
        if (container) {
            // Verificar si hay contenido que desplazar hacia la izquierda
            this.canScrollLeft = container.scrollLeft > 0;
            
            // Verificar si hay contenido que desplazar hacia la derecha
            this.canScrollRight = container.scrollLeft + container.clientWidth < container.scrollWidth;
        }
    }
}